import React, { useMemo } from "react";
import { ThemeProvider } from "@emotion/react";
import { toCSSVar, useTheme } from "@chakra-ui/react";
import { isMobile } from "react-device-detect";

interface Props {
  children: React.ReactNode;
}

// 모바일 기기에서도 그대로 두는 breakpoint — sm(320px)은 모든 휴대폰 폭에서 이미 켜져 있어
// 밀어내면 오히려 지금 휴대폰 화면이 바뀐다. md 이상만 데스크톱 배치의 기준이다.
const KEPT_BREAKPOINT_KEYS = new Set(["base", "sm"]);

// 실제 화면 폭으로는 닿을 수 없는 오프셋. 순서를 지키려고 원래 값에 더한다.
const UNREACHABLE_OFFSET_PX = 100000;

// px·em이 섞여 있다(앱 테마는 px, Chakra 기본의 2xl은 em). Chakra는 정렬할 때 단위를 무시하고
// parseInt로 비교하므로, 밀어낸 값은 모두 px로 맞춰 순서가 꼬이지 않게 한다.
const toPx = (value: string) =>
  value.endsWith("em") ? parseFloat(value) * 16 : parseFloat(value);

const toMobileDeviceBreakpoints = (breakpoints: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(breakpoints).map(([key, value]) =>
      KEPT_BREAKPOINT_KEYS.has(key)
        ? [key, value]
        : [key, `${toPx(value) + UNREACHABLE_OFFSET_PX}px`]
    )
  );

/**
 * 모바일 기기에서는 화면 폭과 관계없이 패스픽 화면을 모바일 배치로 그린다.
 *
 * 앱 틀(private-layout)은 UA(react-device-detect)로 BrowserView/MobileView를 고르는데,
 * 패스픽 화면 안쪽은 Chakra breakpoint(md = 768px)로 모바일·PC 배치를 나눈다.
 * 갤럭시 폴드를 펼치거나 태블릿처럼 768px을 넘는 모바일 기기에서는 두 기준이 엇갈려,
 * 모바일 틀(햄버거 메뉴) 안에 PC용 인라인 필터 줄이 펼쳐지고 필터 버튼은 사라졌다.
 * 게다가 헤더·플로팅 선택 바·페이지네이션이 서로 짝을 맞춰 배치를 나누므로(예: 모바일에서는
 * 선택 삭제가 헤더 대신 선택 바로 간다) 일부만 고치면 기능이 빠진다.
 *
 * 그래서 컴포넌트마다 분기를 고치지 않고, 이 영역의 테마에서 md 이상 breakpoint를 닿을 수 없는
 * 값으로 밀어낸다. 반응형 prop(`{ base, md }`)과 useBreakpointValue가 모두 테마의 breakpoint를
 * 읽으므로 모바일 기기에서는 항상 base 값이 적용된다. 데스크톱은 지금처럼 폭으로 나뉜다.
 * 포털로 뜨는 모달도 React 트리 안이라 같은 테마를 받는다.
 */
function PathPickMobileDeviceBreakpoints({ children }: Props) {
  const theme = useTheme();

  // toCSSVar가 기존 계산값(__breakpoints 등)을 버리고 바뀐 breakpoints로 다시 계산한다.
  const mobileDeviceTheme = useMemo(
    () =>
      isMobile
        ? toCSSVar({
            ...theme,
            breakpoints: toMobileDeviceBreakpoints(theme.breakpoints),
          })
        : null,
    [theme]
  );

  if (!mobileDeviceTheme) {
    return <>{children}</>;
  }

  return <ThemeProvider theme={mobileDeviceTheme}>{children}</ThemeProvider>;
}

export default PathPickMobileDeviceBreakpoints;
